import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"
import DatalakeService from "src/services/datalake-service"

const ParameterDatalakeCETIPAliasEventosTable = (props) => {
    const dataLakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })
    // Volta o rodapé da tabela para a página 1 sempre que os dados recarregam a partir do offset 0
    const [resetPaginationKey, setResetPaginationKey] = useState(0)

    useEffect(() => {
        loadData()
    }, [])

    const handleError = (err) => {
        if (props.callbackError) {
            props.callbackError(err)
        }
    }

    const loadData = () => {
        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
            setResetPaginationKey((key) => key + 1)
        }).catch(handleError).finally(() => {
            props.loading(false)
        })
    }

    const createRow = async () => {
        const body = {
            TipoInstrumentoCETIP: 'N/A',
            CodigoOperacaoCETIP: 0,
            CodigoStatusCETIP: 0,
            IDEvento: 0,
            Inativo: false
        }

        props.loading(true)
        return dataLakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData()
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID, TipoInstrumentoCETIP, CodigoOperacaoCETIP, CodigoStatusCETIP, ModalidadeStatusCETIP, IDEvento, Observacao, Inativo } = newRow

        const body = {
            TipoInstrumentoCETIP: TipoInstrumentoCETIP,
            CodigoOperacaoCETIP: CodigoOperacaoCETIP,
            CodigoStatusCETIP: CodigoStatusCETIP,
            ModalidadeStatusCETIP: ModalidadeStatusCETIP,
            IDEvento: IDEvento,
            Observacao: Observacao,
            Inativo: Inativo
        }

        props.loading(true)
        return dataLakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        const { ID } = row
        return dataLakeService.deleteTableEndpoint(props.endpoint, ID).then(() => {
            props.loading(false)
            const listRemoved = data.filter((item) => item.ID !== ID)
            setData(listRemoved)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
            setResetPaginationKey((key) => key + 1)
        }).catch(handleError).finally(() => {
            props.loading(false)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        dataLakeService.paginate(endpoint).then((response) => {
            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData([])
            setData(list)
        }).catch(handleError).finally(() => {
            props.loading(false)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80,
        },
        {
            field: 'TipoInstrumentoCETIP',
            headerName: 'TipoInstrumentoCETIP',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CodigoOperacaoCETIP',
            headerName: 'CodigoOperacaoCETIP',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CodigoStatusCETIP',
            headerName: 'CodigoStatusCETIP',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'ModalidadeStatusCETIP',
            headerName: 'ModalidadeStatusCETIP',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'IDEvento',
            headerName: 'IDEvento',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'Observacao',
            headerName: 'Observacao',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'Inativo',
            headerName: 'Inativo',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            type: 'boolean',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                filterFunction={onFilter}
                pagination={pagination}
                onChangePage={changePage}
                resetPaginationKey={resetPaginationKey}
                customFilter={true} />
        </div>
    )
}

ParameterDatalakeCETIPAliasEventosTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func,
}

export default ParameterDatalakeCETIPAliasEventosTable